import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  CrudActionButton,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import apiService from 'library/api-service';
import { Grid2 as Grid } from '@mui/material';
import { renderDataGridLocaleText } from 'utils';
import { useSingleRowSelectionGuard } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import gibTypesActions from 'store/features/gib-types/actions';
import { CreateGibTypeDialog, UpdateGibTypeDialog } from './components';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { selectGibTypes, selectGibTypesIsEmpty, selectGibTypesLoading } from 'store/features/gib-types/selector';

const GibTypes = () => {
  // other hooks
  const intl = useIntl();
  const requireSingleRowSelection = useSingleRowSelectionGuard();

  // redux hooks
  const dispatch = useAppDispatch();
  const gibTypes = useAppSelector(selectGibTypes);
  const loading = useAppSelector(selectGibTypesLoading);
  const isEmptyGibTypes = useAppSelector(selectGibTypesIsEmpty);

  // useState hooks
  const [openDeleteGibType, setOpenDeleteGibType] = useState(false);
  const [openCreateGibType, setOpenCreateGibType] = useState(false);
  const [openUpdateGibType, setOpenUpdateGibType] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'gibTypes.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const refresh = async () => {
    dispatch(gibTypesActions.getGibTypes());
  };

  // sync helper functions
  const handleCloseDeleteGibType = () => setOpenDeleteGibType(false);
  const handleClickOpenDeleteGibType = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    // If the check has been passed successfully...
    setOpenDeleteGibType(true);
  };

  const handleCloseCreateGibType = () => setOpenCreateGibType(false);
  const handleClickOpenCreateGibType = () => setOpenCreateGibType(true);

  const handleCloseUpdateGibType = () => setOpenUpdateGibType(false);
  const handleClickOpenUpdateGibType = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    // If the check has been passed successfully...
    setOpenUpdateGibType(true);
  };

  // useEffect hooks
  useEffect(() => {
    if (!gibTypes.length && !isEmptyGibTypes) {
      dispatch(gibTypesActions.getGibTypes());
    }
  }, [gibTypes, isEmptyGibTypes, dispatch]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['create-gib-method']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="create" onClick={handleClickOpenCreateGibType}>
              <FormattedMessage id="gibTypes.createGibType" defaultMessage="GİB Yöntemi Oluştur" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-gib-method']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="update" onClick={handleClickOpenUpdateGibType}>
              <FormattedMessage id="gibTypes.updateGibType" defaultMessage="GİB Yöntemi Düzenle" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-gib-method']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="delete" onClick={handleClickOpenDeleteGibType}>
              <FormattedMessage id="gibTypes.deleteGibType" defaultMessage="GİB Yöntemi Sil" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateGibTypeDialog open={openCreateGibType} handleClose={handleCloseCreateGibType} refresh={refresh} />
      <UpdateGibTypeDialog
        open={openUpdateGibType}
        gibTypeId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateGibType}
        refresh={refresh}
      />
      <DeleteDialog
        open={openDeleteGibType}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteGibType}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="gibTypes.deleteGibType" defaultMessage="GİB Yöntemi Sil" />}
        subTitle={
          <FormattedMessage
            id="gibTypes.deleteGibTypeRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra GİB Yöntemi veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteGibType}
        successMessage={
          <FormattedMessage
            id="gibTypes.deleteGibTypeCompleted"
            defaultMessage="Seçili GİB Yöntemini silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          rows={gibTypes}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default GibTypes;
